import { useRef, useState } from 'react';
import { api } from '../../lib/api';
import { T, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import {
  AsyncButton, Badge, Button, Card, Checkbox, cx, Empty, ErrorBox, Field, Input, Loading, Select, Textarea, useToast,
} from '../../ui/components';

/**
 * Coaching conversations on the client file. Recording only starts once the coach has ticked that the
 * client agreed, because that is both the law and the decent thing to do.
 */
export function CoachingTab({ personId }: { personId: string }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const list = useApi(() => api<any[]>(`${T(gym.slug)}/admin/persons/${personId}/coaching`), [personId]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState<any | null>(null);
  const [uploadTarget, setUploadTarget] = useState('transcript');
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const open = async (id: string): Promise<void> => {
    setOpenId(id);
    setDraft(await api(`${T(gym.slug)}/admin/coaching/${id}`));
  };

  const save = async (patch: Record<string, unknown>): Promise<void> => {
    const updated = await api(`${T(gym.slug)}/admin/coaching/${openId}`, { method: 'PUT', body: patch });
    setDraft(updated);
    list.reload();
  };

  const startRecording = async (): Promise<void> => {
    if (!draft?.consentGivenAt) { toast.error(i18n.t('coaching.consent_first')); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunks.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => chunks.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(',')[1]);
          reader.readAsDataURL(blob);
        });
        try {
          setDraft(await api(`${T(gym.slug)}/admin/coaching/${openId}/recording`, { body: { dataBase64: base64, contentType: 'audio/webm', seconds } }));
          toast.ok(i18n.t('coaching.recording_saved'));
          list.reload();
        } catch (e) { toast.error(e); }
      };
      rec.start();
      recorder.current = rec;
      setRecording(true);
      setSeconds(0);
      timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch { toast.error(i18n.t('coaching.no_microphone')); }
  };

  const stopRecording = (): void => {
    recorder.current?.stop();
    setRecording(false);
    if (timer.current) clearInterval(timer.current);
  };

  if (list.error) return <ErrorBox error={list.error} onRetry={list.reload} />;
  if (!list.data) return <Loading />;

  return (
    <div className="stack">
      <Card title={i18n.t('coaching.title')} actions={
        <AsyncButton size="sm" onClick={async () => {
          const r = await api(`${T(gym.slug)}/admin/persons/${personId}/coaching`, { body: { heldAt: new Date().toISOString() } });
          list.reload();
          await open(r.id);
        }}>+ {i18n.t('coaching.new')}</AsyncButton>
      }>
        <p className="small muted">{i18n.t('coaching.explain')}</p>
        {list.data.length === 0 ? <Empty icon="🗣">{i18n.t('coaching.none')}</Empty> : list.data.map((c) => (
          <div key={c.id} className="row between wrap">
            <div>
              <button type="button" className="link" onClick={() => open(c.id)}>
                <strong>{c.title || i18n.t(`coaching.kind_${c.kind}`)}</strong>
              </button>
              <div className="small muted">
                {i18n.dateTime(c.heldAt)}{c.durationMin ? ` · ${c.durationMin} min` : ''}
                {c.consentGivenAt ? ` · ${i18n.t('coaching.consent_on_file')}` : ` · ${i18n.t('coaching.no_consent')}`}
              </div>
            </div>
            <div className="row gap-sm">
              {c.hasRecording && <Badge value="recorded" label={i18n.t('coaching.has_recording')} />}
              <Badge value={c.sharedAt ? 'good' : 'neutral'} label={i18n.t(c.sharedAt ? 'coaching.shared' : 'coaching.not_shared')} />
            </div>
          </div>
        ))}
      </Card>

      {draft && openId && (
        <Card title={draft.title || i18n.t(`coaching.kind_${draft.kind}`)} actions={<Button size="sm" variant="ghost" onClick={() => { setDraft(null); setOpenId(null); }}>{i18n.t('common.close')}</Button>}>
          <div className="grid-2">
            <Field label={i18n.field('title')}><Input value={draft.title ?? ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} onBlur={() => save({ title: draft.title })} /></Field>
            <Field label={i18n.field('kind')}>
              <Select value={draft.kind} onChange={(e) => save({ kind: e.target.value })}
                options={['mind_body', 'lifestyle', 'nutrition', 'work_life', 'sport', 'other'].map((k) => ({ value: k, label: i18n.t(`coaching.kind_${k}`) }))} />
            </Field>
          </div>

          <Card title={i18n.t('coaching.consent')} className={cx(!draft.consentGivenAt && 'risk risk-medium')}>
            {draft.consentGivenAt ? (
              <div className="row between wrap">
                <span className="small">{i18n.t('coaching.consent_given', { date: i18n.dateTime(draft.consentGivenAt), method: i18n.t(`coaching.method_${draft.consentMethod ?? 'verbal'}`) })}</span>
                <AsyncButton size="sm" variant="secondary" confirm={i18n.t('coaching.withdraw_confirm')} onClick={async () => {
                  setDraft(await api(`${T(gym.slug)}/admin/coaching/${openId}/consent`, { method: 'DELETE' }));
                  toast.ok(i18n.t('coaching.withdrawn'));
                }}>{i18n.t('coaching.withdraw')}</AsyncButton>
              </div>
            ) : (
              <>
                <p className="small">{i18n.t('coaching.consent_ask')}</p>
                <div className="row gap-sm wrap">
                  {(['verbal', 'in_app', 'written'] as const).map((m) => (
                    <AsyncButton key={m} size="sm" onClick={async () => {
                      setDraft(await api(`${T(gym.slug)}/admin/coaching/${openId}/consent`, { body: { method: m } }));
                      toast.ok(i18n.t('coaching.consent_saved'));
                    }}>{i18n.t(`coaching.method_${m}`)}</AsyncButton>
                  ))}
                </div>
              </>
            )}
          </Card>

          <Card title={i18n.t('coaching.recording')}>
            {draft.hasRecording ? (
              <div className="row between wrap">
                <span className="small">{i18n.t('coaching.recording_stored')}</span>
                <div className="row gap-sm">
                  <AsyncButton size="sm" variant="secondary" onClick={async () => {
                    setDraft(await api(`${T(gym.slug)}/admin/coaching/${openId}/transcribe`, { body: {} }));
                    toast.ok(i18n.t('coaching.transcribed'));
                  }}>{i18n.t('coaching.transcribe')}</AsyncButton>
                  <AsyncButton size="sm" variant="danger" confirm={i18n.t('coaching.delete_recording_confirm')} onClick={async () => {
                    setDraft(await api(`${T(gym.slug)}/admin/coaching/${openId}/recording`, { method: 'DELETE' }));
                    toast.ok(i18n.t('coaching.recording_deleted'));
                  }}>{i18n.t('coaching.delete_recording')}</AsyncButton>
                </div>
              </div>
            ) : recording ? (
              <div className="row between">
                <span className="warn">● {i18n.t('coaching.recording_now', { seconds })}</span>
                <Button onClick={stopRecording}>{i18n.t('coaching.stop')}</Button>
              </div>
            ) : (
              <div className="row gap-sm wrap">
                <Button disabled={!draft.consentGivenAt} onClick={startRecording}>{i18n.t('coaching.start')}</Button>
                <span className="small muted">{i18n.t('coaching.retention_note')}</span>
              </div>
            )}
          </Card>

          <Card title={i18n.t('coaching.upload')}>
            <p className="small muted">{i18n.t('coaching.upload_hint')}</p>
            <div className="row gap-sm wrap">
              <Select value={uploadTarget} aria-label={i18n.t('coaching.upload_target')}
                onChange={(e) => setUploadTarget(e.target.value)}
                options={[
                  { value: 'transcript', label: i18n.t('coaching.as_transcript') },
                  { value: 'summary', label: i18n.t('coaching.as_summary') },
                  { value: 'notes', label: i18n.t('coaching.as_notes') },
                ]} />
              <input type="file" accept=".txt,.md,.vtt,.srt,.json,.docx" aria-label={i18n.t('coaching.upload')} onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                try {
                  const base64 = await new Promise<string>((resolve) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(String(reader.result).split(',')[1]);
                    reader.readAsDataURL(f);
                  });
                  setDraft(await api(`${T(gym.slug)}/admin/coaching/${openId}/upload`, {
                    body: { dataBase64: base64, filename: f.name, contentType: f.type, target: uploadTarget, append: true },
                  }));
                  toast.ok(i18n.t('coaching.uploaded'));
                  list.reload();
                } catch (err) { toast.error(err); }
                e.target.value = '';
              }} />
            </div>
          </Card>

          <Field label={i18n.t('coaching.transcript')} hint={i18n.t('coaching.transcript_hint')}>
            <Textarea rows={5} value={draft.transcript ?? ''} onChange={(e) => setDraft({ ...draft, transcript: e.target.value })} onBlur={() => save({ transcript: draft.transcript })} />
          </Field>
          <AsyncButton size="sm" variant="secondary" disabled={!draft.transcript} onClick={async () => {
            setDraft(await api(`${T(gym.slug)}/admin/coaching/${openId}/summary`, { body: {} }));
            toast.ok(i18n.t('coaching.summary_written'));
          }}>{i18n.t('coaching.write_summary')}</AsyncButton>

          <Field label={i18n.t('coaching.summary')} hint={i18n.t('coaching.summary_hint')}>
            <Textarea rows={5} value={draft.summary ?? ''} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} onBlur={() => save({ summary: draft.summary })} />
          </Field>
          <Field label={i18n.t('coaching.actions')} hint={i18n.t('coaching.actions_hint')}>
            <Textarea rows={3} value={(draft.actions ?? []).join('\n')}
              onChange={(e) => setDraft({ ...draft, actions: e.target.value.split('\n') })}
              onBlur={() => save({ actions: (draft.actions ?? []).map((a: string) => a.trim()).filter(Boolean) })} />
          </Field>
          <Field label={i18n.t('coaching.private_notes')} hint={i18n.t('coaching.private_notes_hint')}>
            <Textarea rows={4} value={draft.privateNotes ?? ''} onChange={(e) => setDraft({ ...draft, privateNotes: e.target.value })} onBlur={() => save({ privateNotes: draft.privateNotes })} />
          </Field>

          <div className="row between wrap">
            <Checkbox label={i18n.t('coaching.share_transcript')} checked={Boolean(draft.shareTranscript)} onChange={(v) => save({ shareTranscript: v })} />
            <AsyncButton onClick={() => save({ shared: !draft.sharedAt })}>
              {i18n.t(draft.sharedAt ? 'coaching.unshare' : 'coaching.share')}
            </AsyncButton>
          </div>
          {draft.clientReadAt && <p className="small muted">{i18n.t('coaching.client_read', { date: i18n.dateTime(draft.clientReadAt) })}</p>}
        </Card>
      )}
    </div>
  );
}
